import { useEffect, useState } from 'react';
import type {
  BrouillonItem,
  ChangementItem,
  Item,
  ItemType,
  MembrePublic,
} from '@camellie/shared';
import { CATEGORIES_ENFANT, estAdulte, RECURRENCES } from '@camellie/shared';
import { aujourdhui, champsEcheance, echeanceDepuisChamps, MINUTES_RAPPEL } from '../lib/format';
import { Confirmation } from './Confirmation';

interface Props {
  /** L'item a modifier, ou `null` pour en creer un. */
  item: Item | null;
  /** Type a creer quand `item` vaut null. Ignore en modification. */
  typeNouveau?: ItemType;
  /** Date pre-remplie a la creation, ex. le jour choisi dans l'agenda. */
  dateParDefaut?: string;
  /**
   * Mode enfant : la fiche concerne cet enfant. « Pour qui » cede la place a
   * « Categorie », et l'element n'est assigne a aucun adulte — preparer un
   * enfant n'est pas une tache que l'on se confie, c'est le travail du matin.
   */
  enfant?: MembrePublic;
  membres: MembrePublic[];
  moi: string;
  onEnregistrer: (id: string, champs: ChangementItem) => void;
  onCreer: (brouillon: BrouillonItem) => void;
  onSupprimer: (id: string) => void;
  onFermer: () => void;
}

/**
 * Fiche d'un element, en feuille remontant du bas. Elle sert a la fois a
 * creer et a modifier.
 *
 * Un seul composant pour les deux : les champs sont identiques, et deux
 * formulaires separes divergeraient a la premiere evolution — on ajouterait
 * « lieu » d'un cote en oubliant l'autre.
 *
 * Les champs date et heure sont des `<input type="date">` et
 * `<input type="time">` natifs : iOS ouvre ses propres selecteurs, deja
 * connus de tout le monde, accessibles, et qui ne coutent aucune dependance.
 * Un calendrier maison serait plus joli et moins bien.
 *
 * L'heure reste facultative. Sans elle, l'element est « dans la journee » et
 * ne declenche aucun rappel — il n'y a pas d'instant a annoncer.
 *
 * Pas de focus automatique sur le titre : sur iPhone le clavier recouvrirait
 * aussitot les champs qu'on vient justement ouvrir pour les remplir. La
 * touche Entree valide, donc une saisie rapide reste possible.
 */
export function FicheItem({
  item,
  typeNouveau = 'tache',
  dateParDefaut,
  enfant,
  membres,
  moi,
  onEnregistrer,
  onCreer,
  onSupprimer,
  onFermer,
}: Props) {
  const creation = item === null;
  const [typeChoisi, setTypeChoisi] = useState<ItemType>(item?.type ?? typeNouveau);
  const type = enfant ? typeChoisi : (item?.type ?? typeNouveau);
  const initial = champsEcheance(item?.du_le ?? null);
  const paysage = (item?.payload ?? {}) as { lieu?: string; notes?: string };

  const [titre, setTitre] = useState(item?.titre ?? '');
  const [assigne, setAssigne] = useState<string | null>(item?.assigne_a ?? null);
  // Un rendez-vous sans date n'existe pas : on prefixe avec aujourd'hui pour
  // eviter de faire saisir le mois et l'annee d'un rendez-vous de cet
  // apres-midi.
  const [date, setDate] = useState(
    initial.date || (creation ? (dateParDefaut ?? (type === 'rdv' ? aujourdhui() : '')) : ''),
  );
  const [heure, setHeure] = useState(initial.heure);
  const [recurrence, setRecurrence] = useState<string | null>(item?.recurrence ?? null);
  const [lieu, setLieu] = useState(paysage.lieu ?? '');
  const [notes, setNotes] = useState(paysage.notes ?? '');
  const [suppression, setSuppression] = useState(false);

  // Echap ferme la feuille : utile au clavier, sans effet sur iPhone.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  function valider() {
    const propre = titre.trim();
    if (propre === '') return;
    const du_le = echeanceDepuisChamps(date, heure);
    const payload: Record<string, unknown> = {
      lieu: lieu.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    // L'enfant concerne voyage dans le payload : aucune colonne dediee,
    // aucune migration, conformement au principe de la table generique.
    if (enfant) payload.pour = enfant.id;

    if (creation) {
      onCreer({
        type,
        titre: propre,
        assigne_a: assigne,
        du_le,
        // Une repetition sans echeance n'a rien a repeter.
        recurrence: du_le ? recurrence : null,
        payload,
      });
    } else {
      onEnregistrer(item.id, {
        titre: propre,
        assigne_a: assigne,
        du_le,
        recurrence: du_le ? recurrence : null,
        // On repart du payload existant : le remplacer effacerait ce que
        // d'autres fonctionnalites y auront depose (filiation d'une occurrence
        // repetee, provenance d'un courriel).
        payload: { ...(item.payload as Record<string, unknown>), ...payload },
      });
    }
    onFermer();
  }

  function supprimer() {
    if (item) onSupprimer(item.id);
    onFermer();
  }

  return (
    <div className="voile" onClick={onFermer} role="presentation">
      {/* La feuille arrete la propagation : cliquer dedans ne ferme pas. */}
      <div className="feuille" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="feuille-poignee" aria-hidden="true" />

        <h2 className="feuille-titre">
          {!creation
            ? 'Modifier'
            : enfant
              ? 'Pour ' + enfant.prenom
              : type === 'rdv'
                ? 'Nouveau rendez-vous'
                : 'Nouvelle tache'}
        </h2>

        <label className="champ">
          <span>Titre</span>
          <input
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') valider();
            }}
            placeholder={type === 'rdv' ? 'Dentiste, entrainement…' : 'Sortir les poubelles…'}
            enterKeyHint="done"
            autoComplete="off"
            autoCapitalize="sentences"
          />
        </label>

        {enfant ? (
          <div className="champ">
            <span>Categorie</span>
            <div className="pastilles">
              {CATEGORIES_ENFANT.map((c) => (
                <button
                  key={c.type}
                  type="button"
                  className={'pastille-choix' + (type === c.type ? ' active' : '')}
                  onClick={() => setTypeChoisi(c.type)}
                >
                  {c.libelle}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="champ">
            <span>Pour qui</span>
            <div className="pastilles">
              <button
                type="button"
                className={'pastille-choix' + (assigne === null ? ' active' : '')}
                onClick={() => setAssigne(null)}
              >
                Ensemble
              </button>
              {/* Les enfants n'apparaissent jamais ici : on ne leur confie pas
                  de tache, on s'occupe d'eux. */}
              {membres.filter(estAdulte).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={'pastille-choix' + (assigne === m.id ? ' active' : '')}
                  onClick={() => setAssigne(m.id)}
                >
                  <span className="point-membre" style={{ background: m.couleur }} />
                  {m.id === moi ? 'Moi' : m.prenom}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="champ-double">
          <label className="champ">
            <span>Date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="champ">
            <span>Heure</span>
            <input
              type="time"
              value={heure}
              onChange={(e) => setHeure(e.target.value)}
              disabled={date === ''}
            />
          </label>
        </div>

        {date !== '' && (
          <div className="champ">
            <span>Repeter</span>
            <div className="pastilles">
              {RECURRENCES.map((r) => (
                <button
                  key={r.libelle}
                  type="button"
                  className={'pastille-choix' + (recurrence === r.valeur ? ' active' : '')}
                  onClick={() => setRecurrence(r.valeur)}
                >
                  {r.libelle}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="champ">
          <span>Lieu</span>
          <input
            value={lieu}
            onChange={(e) => setLieu(e.target.value)}
            placeholder="Clinique, ecole, aréna…"
            autoComplete="off"
            autoCapitalize="words"
          />
        </label>

        <label className="champ">
          <span>Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Ce qu il faut apporter, un numero, un detail…"
            autoCapitalize="sentences"
          />
        </label>

        <p className="note">
          {date === ''
            ? 'Sans date : reste dans la liste, hors du brief.'
            : heure === ''
              ? 'Sans heure : dans la journee, aucun rappel.'
              : 'Rappel ' + MINUTES_RAPPEL.join(' et ') + ' minutes avant.'}
          {recurrence !== null && ' Une nouvelle sera creee une fois celle-ci cochee.'}
        </p>

        <div className="feuille-actions">
          <button type="button" className="bouton discret" onClick={onFermer}>
            Annuler
          </button>
          <button
            type="button"
            className="bouton primaire"
            onClick={valider}
            disabled={titre.trim() === ''}
          >
            {creation ? 'Ajouter' : 'Enregistrer'}
          </button>
        </div>

        {!creation && (
          <button
            type="button"
            className="bouton danger pleine-largeur"
            onClick={() => setSuppression(true)}
          >
            Supprimer
          </button>
        )}
      </div>

      {suppression && item && (
        <Confirmation
          message={'Supprimer « ' + item.titre + ' » ?'}
          action="Supprimer"
          danger
          onConfirmer={supprimer}
          onAnnuler={() => setSuppression(false)}
        />
      )}
    </div>
  );
}
